import React, { useEffect, useRef, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Box,
} from '@mui/material';
import {
  Add,
  BookmarksOutlined,
  MicNoneRounded,
  SendRounded,
  Stop,
} from '@mui/icons-material';
import { PromptInputShell, PromptTextarea } from './styledComponents';
import { presetQueries as defaultPresetQueries } from './presetQueries';

export const PromptInput = ({ 
  value, 
  onChange, 
  onSubmit, 
  onKeyDown, 
  isLoading, 
  disabled = false,
  elevated = false,
  placeholder = "Message Lucro Assistant...",
  onStop,
  onPresetSelect,
  presets = defaultPresetQueries,
}) => {
  const textareaRef = useRef(null);
  const shellRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuWidth, setMenuWidth] = useState(360);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.setAttribute('style', 'height:auto;');
      textareaRef.current.setAttribute(
        'style',
        `height:${Math.min(textareaRef.current.scrollHeight, elevated ? 168 : 168)}px`,
      );
    }
  }, [value, elevated]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      onSubmit?.();
      return; // Don't call parent onKeyDown to prevent double submission
    }
    onKeyDown?.(event);
  };

  const canSubmit = ((value || '').trim()) && !isLoading && !disabled;

  const handleMenuOpen = (event) => {
    if (disabled) {
      return;
    }
    const anchorTarget = shellRef.current || event.currentTarget;
    if (shellRef.current) {
      const { offsetWidth } = shellRef.current;
      if (offsetWidth) {
        setMenuWidth(Math.max(offsetWidth, 360));
      }
    }
    setAnchorEl(anchorTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePresetClick = (preset) => {
    onPresetSelect?.(preset.query);
    handleMenuClose();
    // focus and move cursor to end after React updates value
    setTimeout(() => {
      if (textareaRef.current) {
        const element = textareaRef.current;
        element.focus();
        const length = preset.query.length;
        element.setSelectionRange(length, length);
      }
    }, 0);
  };

  return (
    <PromptInputShell elevated={elevated} ref={shellRef}>
      <Tooltip title="Add more actions" placement="top">
        <span>
          <IconButton
            size="small"
            sx={{ color: '#6B7280', mr: 1 }}
            disabled={disabled}
          >
            <Add fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Insert a saved query" placement="top">
        <span>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ color: '#6B7280', mr: 1.5 }}
            disabled={disabled}
            aria-haspopup="true"
            aria-expanded={Boolean(anchorEl)}
            aria-controls={Boolean(anchorEl) ? 'preset-query-menu' : undefined}
          >
            <BookmarksOutlined fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <PromptTextarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onKeyDown={handleKeyDown}
        minRows={1}
        maxRows={elevated ? 6 : 4}
        placeholder={placeholder}
        disabled={disabled || isLoading}
      />
      <IconButton size="small" sx={{ color: '#6B7280', mr: 1.5 }}>
        <MicNoneRounded />
      </IconButton>
      <Menu
        id="preset-query-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        MenuListProps={{ dense: true, sx: { maxHeight: 420 } }}
        PaperProps={{
          sx: {
            width: menuWidth,
            maxWidth: menuWidth,
            borderRadius: '18px 18px 10px 10px',
            boxShadow: '0 16px 32px rgba(15, 23, 42, 0.18)',
            mt: 0,
          },
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Quick start prompts
          </Typography>
          <Typography variant="caption" color="text.disabled">
            Click to populate the input, then press Enter to run.
          </Typography>
        </Box>
        <Divider />
        {presets.map((preset) => {
          const normalizedPreset =
            typeof preset === 'string' ? { query: preset, category: null } : preset;

          return (
            <MenuItem
              key={normalizedPreset.query}
              onClick={() => handlePresetClick(normalizedPreset)}
              sx={{ alignItems: 'flex-start', py: 1.2 }}
            >
              <ListItemText
                primary={
                  <Typography variant="body2" color="text.primary">
                    {normalizedPreset.query}
                  </Typography>
                }
                secondary={
                  normalizedPreset.category ? (
                    <Typography variant="caption" color="text.secondary" display="block">
                      {normalizedPreset.category}
                    </Typography>
                  ) : null
                }
              />
            </MenuItem>
          );
        })}
      </Menu>
      {isLoading ? (
        <IconButton
          size="small"
          onClick={onStop}
          sx={{
            backgroundColor: '#DC3545',
            color: '#FFFFFF',
            '&:hover': {
              backgroundColor: '#C82333',
            },
            boxShadow: '0 4px 12px rgba(220, 53, 69, 0.3)',
          }}
        >
          <Stop fontSize="small" />
        </IconButton>
      ) : (
        <IconButton
          size="small"
          onClick={onSubmit}
          disabled={!canSubmit}
          sx={{
            backgroundColor: canSubmit ? '#627CFB' : '#E5E7EB',
            color: canSubmit ? '#FFFFFF' : '#9CA3AF',
            '&:hover': {
              backgroundColor: canSubmit ? '#4E64E0' : '#E5E7EB',
            },
            '&.Mui-disabled': {
              backgroundColor: '#E5E7EB',
              color: '#9CA3AF',
            },
            boxShadow: canSubmit
              ? '0 4px 12px rgba(98, 124, 251, 0.3)'
              : 'none',
          }}
        >
          <SendRounded fontSize="small" />
        </IconButton>
      )}
    </PromptInputShell>
  );
};
